import moment from 'moment'
import _ from 'lodash'

import EstimatesRepository from './EstimatesRepository'
import Utils from './Utils'

export default class DbEstimateEditor {
  
  initialize = props => {
    if (props.estimate && props.teamId
      && (props.teamId !== this.teamId || props.estimate !== this.estimate)) {
      this.teamId = props.teamId
      this.estimate = props.estimate
      this.estimateItems = null
      this.estimatePhotos = null
      this.customerInfo = null
      this.referencedEvents = null
      this.estimateListener = EstimatesRepository.addEstimateListener(
        this.teamId, this.estimate.id, this.onEstimateValue)
      this.estimateItemsListener = EstimatesRepository.addEstimateItemsListener(
        this.teamId, this.estimate.id, this.onEstimateItemsValue)
      this.customerListener = EstimatesRepository.addCustomerListener(
        this.teamId, this.estimate.customerId, this.onCustomerValue)
    }
  }

  getTeamId = () => this.teamId

  getBillingEntity = () => this.estimate

  release = () => {
    if (this.estimateListener) {
      EstimatesRepository.removeEstimateListener(
        this.teamId, this.estimate.id, this.onEstimateValue)
      this.estimateListener = null
    }
    if (this.estimateItemsListener) {
      EstimatesRepository.removeEstimateItemsListener(
        this.teamId, this.estimate.id, this.onEstimateItemsValue)
      this.estimateItemsListener = null
    }
    if (this.customerListener) {
      EstimatesRepository.removeCustomerListener(
        this.teamId, this.estimate.customerId, this.onCustomerValue)
      this.customerListener = null
    }
  }

  notifyValueUpdatedListener = () => {
    if (this.valueUpdatedListener) {
      this.valueUpdatedListener()
    }
  }

  addValueUpdatedListener = listener => {
    this.valueUpdatedListener = listener
  }

  onCustomerValue = snapshot => {
    this.customerInfo = snapshot && snapshot.val()
    this.notifyValueUpdatedListener()
  }

  onEstimateValue = snapshot => {
    this.estimate = snapshot && snapshot.val() || {}
    this.notifyValueUpdatedListener()
  }

  onEstimateItemsValue = snapshot => {
    const items = snapshot && snapshot.val()
    this.estimateItems = items && _.map(items) || []
    this.notifyValueUpdatedListener()
  }

  getCustomerId = () => this.estimate.customerId
  
  getContactId = () => this.estimate.contactId

  setCustomerInfo = customerInfo => {
    if (this.estimate.customerId !== customerInfo.customerId || this.estimate.contactId !== customerInfo.contactId) {
      if (this.estimate.customerId !== customerInfo.customerId) {
        EstimatesRepository.removeCustomerListener(this.teamId, this.estimate.customerId, this.onCustomerValue)
        EstimatesRepository.addCustomerListener(this.teamId, customerInfo.customerId, this.onCustomerValue)
      }
      EstimatesRepository.updateEstimateCustomerInfo(this.teamId, this.estimate, customerInfo)
    }
  }

  getContactInfo = () => {
    return this.estimate.contactId
      && this.customerInfo
      && this.customerInfo.contactsList
      && this.customerInfo.contactsList[this.estimate.contactId]
  }

  getCustomerInfo = () => {
    // Return a normalized model with customer and contact properties
    return {
      customerId: this.estimate.customerId,
      contactId: this.estimate.contactId,
      customer: {
        id: this.estimate.customerId,
        name: this.customerInfo && this.customerInfo.name
      },
      contact: this.getContactInfo()
    }
  }

  isLoadingItems = () => this.estimateItems === null

  getItems = () => this.estimateItems

  addItem = item => {
    EstimatesRepository.addEstimateItem(this.teamId, this.estimate, item)
    EstimatesRepository.updateEstimateTotal(this.teamId, this.estimate,
      this.estimate.total + Utils.getItemAmount(item))
  }

  deleteItem = item => {
    EstimatesRepository.deleteEstimateItem(this.teamId, this.estimate, item)
    EstimatesRepository.updateEstimateTotal(this.teamId, this.estimate,
      this.estimate.total - Utils.getItemAmount(item))
  }

  updateItemName = (item, name) => {
    item.name = name
    EstimatesRepository.updateItem(this.teamId, this.estimate, item)
  }

  updateItemDescription = (item, description) => {
    item.description = description
    EstimatesRepository.updateItem(this.teamId, this.estimate, item)
  }

  updateItemRate = (item, rate) => {
    const oldAmount = Utils.getItemAmount(item)
    item.rate = rate
    const newAmount = Utils.getItemAmount(item)
    EstimatesRepository.updateItem(this.teamId, this.estimate, item)
    EstimatesRepository.updateEstimateTotal(this.teamId, this.estimate,
      this.estimate.total + newAmount - oldAmount)
  }

  updateItemTaxPercent = (item, taxPercent) => {
    const oldAmount = Utils.getItemAmount(item)
    item.taxPercent = taxPercent
    const newAmount = Utils.getItemAmount(item)
    EstimatesRepository.updateItem(this.teamId, this.estimate, item)
    EstimatesRepository.updateEstimateTotal(this.teamId, this.estimate,
      this.estimate.total + newAmount - oldAmount)
  }

  updateItemQuantity = (item, quantity) => {
    const oldAmount = Utils.getItemAmount(item)
    item.quantity = quantity
    const newAmount = Utils.getItemAmount(item)
    EstimatesRepository.updateItem(this.teamId, this.estimate, item)
    EstimatesRepository.updateEstimateTotal(this.teamId, this.estimate,
      this.estimate.total + newAmount - oldAmount)
  }

  getTotal = () => this.estimate.total

  getTitle = () => {
    return this.estimate.title
  }

  setTitle = title => {
    EstimatesRepository.setEstimateTitle(this.teamId, this.estimate, title)
  }

  getNumber = () => {
    return this.estimate.number
  }

  setNumber = number => {
    EstimatesRepository.setEstimateNumber(this.teamId, this.estimate, number)
  }

  getNotes = () => {
    return this.estimate.notes
  }

  setNotes = notes => {
    EstimatesRepository.setEstimateNotes(this.teamId, this.estimate, notes)
  }

  getPaymentDueDate = () => {
    const result = moment(this.estimate.paymentDueDate)
    return result.isValid() ? result : null
  }

  setPaymentDueDate = date => {
    EstimatesRepository.setEstimatePaymentDueDate(this.teamId, this.estimate, date.toISOString())
  }

  getBillDate = () => {
    const result = moment(this.estimate.estimateDate)
    return result.isValid() ? result : null
  }
  
  setBillDate = date => {
    EstimatesRepository.setEstimateDate(this.teamId, this.estimate, date.toISOString())
  }

  /** Realtime updates, no need for save */
  save = () => {
    return { success: true, estimate: this.estimate }
  }

  delete = () => {
    EstimatesRepository.deleteEstimate(this.teamId, this.estimate)
  }

}



// WEBPACK FOOTER //
// ./src/billing/infra/DbEstimateEditor.js